/**
 * C29 Product Page JavaScript
 * Handles discount code validation and price updates
 */

(function() {
  'use strict';

  // ========================================
  // Discount Code Handler
  // ========================================
  class C29DiscountHandler {
    constructor(container) {
      this.container = container;
      this.input = container.querySelector('[data-discount-input]');
      this.btn = container.querySelector('[data-discount-btn]');
      this.presetBtn = container.querySelector('[data-preset-apply]');
      this.result = container.querySelector('[data-discount-result]');
      this.resultText = container.querySelector('[data-discount-result-text]');
      this.finalPrice = container.querySelector('[data-discount-final-price]');
      this.oldPrice = container.querySelector('[data-discount-old-price]');
      this.priceDisplay = document.querySelector('[data-c29-price-current]');
      this.priceCompare = document.querySelector('[data-c29-price-compare]');
      this.priceSavings = document.querySelector('[data-c29-price-savings]');
      this.priceBlock = document.querySelector('.c29-price-block');

      // Get config from data attributes
      // Parse European number format: "1.099,00" -> 1099.00
      let priceStr = container.dataset.basePrice || '0';
      priceStr = priceStr.replace(/\./g, '').replace(',', '.');
      this.basePrice = parseFloat(priceStr) || 0;
      this.currency = container.dataset.currency || '€';

      // API endpoint for discount validation
      this.apiUrl = container.dataset.discountApi || '';

      // Product ID for product-specific discount validation
      this.productId = container.dataset.productId || '';

      // Fallback: local tiers (kept for offline/no-API scenarios)
      try {
        this.discountTiers = JSON.parse(container.dataset.discountTiers || '[]');
      } catch (e) {
        this.discountTiers = [];
      }

      this.appliedDiscount = 0;
      this.appliedCode = '';
      this.isValidating = false;

      this.init();
    }

    init() {
      if (this.btn) {
        this.btn.addEventListener('click', () => this.applyCode());
      }

      if (this.input) {
        this.input.addEventListener('keypress', (e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            this.applyCode();
          }
        });

        // Listen for input changes to reset when cleared
        this.input.addEventListener('input', () => {
          if (this.input.value.trim() === '' && this.appliedCode) {
            this.clearDiscount();
          }
        });
      }

      // Preset code button
      if (this.presetBtn) {
        this.presetBtn.addEventListener('click', () => this.applyPresetCode());
      }

      // Check URL for discount code
      this.checkUrlDiscount();
    }

    applyPresetCode() {
      const presetCode = this.container.dataset.presetCode;
      const presetAmount = parseFloat(this.container.dataset.presetAmount) || 0;

      if (presetCode && presetAmount > 0) {
        // Fill the input with the preset code
        if (this.input) {
          this.input.value = presetCode;
        }
        // Apply the discount directly
        this.applyDiscount(presetCode, presetAmount);
        // Hide the preset button
        if (this.presetBtn) {
          this.presetBtn.style.display = 'none';
        }
      }
    }

    clearDiscount() {
      this.appliedDiscount = 0;
      this.appliedCode = '';

      // Clear input value to show placeholder
      if (this.input) {
        this.input.value = '';
      }

      // Hide result section
      if (this.result) {
        this.result.className = 'c29-discount-result';
      }

      // Reset main price display to original
      this.resetMainPriceDisplay();

      // Show preset button again if exists
      if (this.presetBtn) {
        this.presetBtn.style.display = '';
      }

      // Clear stored discount
      sessionStorage.removeItem('c29_discount_code');
      localStorage.removeItem('c29_discount_code');
    }

    resetMainPriceDisplay() {
      // Reset main price
      if (this.priceDisplay) {
        this.priceDisplay.textContent = `${this.currency}${this.basePrice.toFixed(2)}`;
        this.priceDisplay.classList.remove('discounted');
      }

      // Hide compare price added by discount
      if (this.priceBlock) {
        const compareEl = this.priceBlock.querySelector('.c29-price-compare');
        const savingsEl = this.priceBlock.querySelector('.c29-price-savings');

        // Only hide if they were dynamically added (not from server)
        if (compareEl && !compareEl.hasAttribute('data-original')) {
          compareEl.style.display = 'none';
        }
        if (savingsEl && !savingsEl.hasAttribute('data-original')) {
          savingsEl.style.display = 'none';
        }
      }
    }

    checkUrlDiscount() {
      const params = new URLSearchParams(window.location.search);
      const urlCode = params.get('discount') || params.get('code');

      // Check URL first, then localStorage for saved discount
      const savedCode = urlCode || localStorage.getItem('c29_discount_code');

      if (savedCode && this.input) {
        this.input.value = savedCode;
        this.applyCode();
      }
    }

    async applyCode() {
      if (!this.input || this.isValidating) return;

      const code = this.input.value.trim().toUpperCase();

      if (!code) {
        this.showError('Please enter a discount code');
        return;
      }

      // If API URL is configured, validate via Cloudflare Worker
      if (this.apiUrl) {
        await this.validateViaApi(code);
      } else {
        // Fallback: local tier matching
        this.validateLocally(code);
      }
    }

    async validateViaApi(code) {
      this.isValidating = true;
      this.showLoading();

      try {
        const requestBody = { code };
        // Include productId for product-specific discount validation
        if (this.productId) {
          requestBody.productId = this.productId;
        }

        const response = await fetch(this.apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();

        if (!data.valid) {
          this.showError(data.error || 'Invalid discount code. Please check and try again.');
          this.resetPrice();
          return;
        }

        // Calculate discount amount based on type
        let discountAmount = 0;

        if (data.type === 'fixed_amount') {
          discountAmount = data.value;
        } else if (data.type === 'percentage') {
          discountAmount = this.basePrice * (data.value / 100);
        } else if (data.type === 'free_shipping') {
          this.showFreeShipping(code, data.title);
          return;
        }

        // Check minimum requirement
        if (data.minimum_amount && this.basePrice < data.minimum_amount) {
          this.showError(`Minimum order of ${this.currency}${data.minimum_amount.toFixed(2)} required for this code.`);
          this.resetPrice();
          return;
        }

        if (discountAmount > 0) {
          this.appliedDiscountType = data.type;
          this.appliedDiscountValue = data.value;
          this.applyDiscount(code, discountAmount);
        } else {
          this.showError('This discount code cannot be applied to this product.');
          this.resetPrice();
        }

      } catch (error) {
        console.error('Discount API error:', error);
        // Fallback to local validation on network error
        this.validateLocally(code);
      } finally {
        this.isValidating = false;
      }
    }

    validateLocally(code) {
      let matchedTier = null;
      for (const tier of this.discountTiers) {
        const tierCodes = tier.codes.split(',').map(c => c.trim().toUpperCase());
        if (tierCodes.includes(code)) {
          matchedTier = tier;
          break;
        }
      }

      if (matchedTier) {
        this.applyDiscount(code, parseFloat(matchedTier.amount));
      } else {
        this.showError('Invalid discount code. Please check and try again.');
        this.resetPrice();
      }
    }

    showLoading() {
      if (this.btn) {
        this.btn.disabled = true;
        this.btn.dataset.originalText = this.btn.textContent;
        this.btn.textContent = '...';
      }
    }

    showFreeShipping(code, title) {
      if (this.result) {
        this.result.className = 'c29-discount-result success';
      }
      if (this.resultText) {
        this.resultText.innerHTML = `
          <div class="c29-discount-confirmed">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <span>Code "<strong>${code}</strong>" applied! Free shipping on this order.</span>
          </div>
        `;
      }
      this.appliedCode = code;
      this.storeDiscountForCheckout(code);
    }

    getDeliveryDateRange() {
      const today = new Date();
      const minDays = 3;
      const maxDays = 7;

      const minDate = new Date(today);
      minDate.setDate(today.getDate() + minDays);

      const maxDate = new Date(today);
      maxDate.setDate(today.getDate() + maxDays);

      const options = { month: 'short', day: 'numeric' };
      const minStr = minDate.toLocaleDateString('en-US', options);
      const maxStr = maxDate.toLocaleDateString('en-US', options);

      return `${minStr} - ${maxStr}`;
    }

    applyDiscount(code, amount) {
      this.restoreButton();
      this.appliedDiscount = amount;
      this.appliedCode = code;
      const newPrice = this.basePrice - amount;

      // Show success result in discount section (no price, just confirmation + delivery)
      if (this.result) {
        this.result.className = 'c29-discount-result success';
      }

      // Update result text with code confirmation and checkout note
      if (this.resultText) {
        let checkoutNote = `${this.currency}${amount.toFixed(2)} will be deducted at checkout.`;
        if (this.appliedDiscountType === 'percentage') {
          checkoutNote = `${this.appliedDiscountValue}% off will be applied at checkout.`;
        }
        this.resultText.innerHTML = `
          <div class="c29-discount-confirmed">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
            </svg>
            <span>Code "<strong>${code}</strong>" applied!</span>
          </div>
          <div class="c29-discount-checkout-note">${checkoutNote}</div>
        `;
      }

      // Hide the price display in result area (we show it in main price area only)
      if (this.finalPrice) {
        this.finalPrice.style.display = 'none';
      }
      if (this.oldPrice) {
        this.oldPrice.style.display = 'none';
      }

      // Hide savings in result area (shown in main price area)
      const savingsEl = this.container.querySelector('[data-discount-savings]');
      if (savingsEl) {
        savingsEl.style.display = 'none';
      }

      // Show delivery estimate
      const deliveryEl = this.container.querySelector('[data-delivery-estimate]');
      if (deliveryEl) {
        const dateRange = this.getDeliveryDateRange();
        deliveryEl.innerHTML = `
          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"/>
          </svg>
          <span>Order now, estimated delivery: <strong>${dateRange}</strong></span>
        `;
        deliveryEl.style.display = 'flex';
      }

      // Update main price display (the price area above)
      this.updateMainPriceDisplay(newPrice, amount);

      // Store for checkout
      this.storeDiscountForCheckout(code);
    }

    updateMainPriceDisplay(newPrice, savedAmount) {
      // Update the main price to show new price
      if (this.priceDisplay) {
        this.priceDisplay.textContent = `${this.currency}${newPrice.toFixed(2)}`;
        this.priceDisplay.classList.add('discounted');
      }

      // Show or create compare price
      if (this.priceBlock) {
        // Check if compare price element exists, if not create it
        let compareEl = this.priceBlock.querySelector('.c29-price-compare');
        if (!compareEl) {
          compareEl = document.createElement('span');
          compareEl.className = 'c29-price-compare';
          compareEl.setAttribute('data-c29-price-compare', '');
          const priceRow = this.priceBlock.querySelector('.c29-price-row');
          if (priceRow && this.priceDisplay) {
            priceRow.insertBefore(compareEl, this.priceDisplay.nextSibling);
          }
        }
        compareEl.textContent = `${this.currency}${this.basePrice.toFixed(2)}`;
        compareEl.style.display = '';

        // Show or create savings element
        let savingsMainEl = this.priceBlock.querySelector('.c29-price-savings');
        if (!savingsMainEl) {
          savingsMainEl = document.createElement('div');
          savingsMainEl.className = 'c29-price-savings';
          savingsMainEl.setAttribute('data-c29-price-savings', '');
          this.priceBlock.appendChild(savingsMainEl);
        }
        savingsMainEl.textContent = `You save ${this.currency}${savedAmount.toFixed(2)}`;
        savingsMainEl.style.display = '';
      }
    }

    restoreButton() {
      if (this.btn && this.btn.dataset.originalText) {
        this.btn.disabled = false;
        this.btn.textContent = this.btn.dataset.originalText;
      }
    }

    showError(message) {
      this.restoreButton();
      this.appliedDiscount = 0;
      this.appliedCode = '';

      if (this.result) {
        this.result.className = 'c29-discount-result error';
      }
      if (this.resultText) {
        this.resultText.textContent = message;
      }
      if (this.finalPrice) {
        this.finalPrice.textContent = '';
        this.finalPrice.style.display = 'none';
      }
      if (this.oldPrice) {
        this.oldPrice.textContent = '';
        this.oldPrice.style.display = 'none';
      }
      // Clear savings text
      const savingsEl = this.container.querySelector('[data-discount-savings]');
      if (savingsEl) {
        savingsEl.textContent = '';
        savingsEl.style.display = 'none';
      }
      // Hide delivery estimate
      const deliveryEl = this.container.querySelector('[data-delivery-estimate]');
      if (deliveryEl) {
        deliveryEl.style.display = 'none';
      }
    }


    storeDiscountForCheckout(code) {
      // Store in sessionStorage and localStorage for persistence
      sessionStorage.setItem('c29_discount_code', code);
      localStorage.setItem('c29_discount_code', code);

      // Apply discount to Shopify session using hidden iframe
      // This silently visits /discount/CODE to bind discount to session
      this.applyDiscountToSession(code);

      // Update add to cart form if exists
      const form = document.querySelector('form[action*="/cart/add"]');
      if (form) {
        let hiddenInput = form.querySelector('input[name="discount"]');
        if (!hiddenInput) {
          hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = 'discount';
          form.appendChild(hiddenInput);
        }
        hiddenInput.value = code;
      }

      // Intercept checkout links to ensure discount is applied
      this.interceptCheckoutLinks(code);
    }

    applyDiscountToSession(code) {
      // Create hidden iframe to apply discount to Shopify session
      const iframe = document.createElement('iframe');
      iframe.style.display = 'none';
      iframe.src = `/discount/${encodeURIComponent(code)}?redirect=/`;
      document.body.appendChild(iframe);

      // Remove iframe after it loads
      iframe.onload = () => {
        setTimeout(() => {
          iframe.remove();
        }, 1000);
      };
    }

    interceptCheckoutLinks(code) {
      // Find and modify all checkout links/buttons
      document.querySelectorAll('a[href*="/checkout"], button[name="checkout"], input[name="checkout"]').forEach(el => {
        if (el.tagName === 'A') {
          const url = new URL(el.href, window.location.origin);
          url.searchParams.set('discount', code);
          el.href = url.toString();
        }
      });

      // Also intercept form submissions to checkout
      document.querySelectorAll('form[action*="/checkout"], form[action*="/cart"]').forEach(form => {
        if (!form.dataset.discountIntercepted) {
          form.dataset.discountIntercepted = 'true';
          form.addEventListener('submit', (e) => {
            const discountCode = localStorage.getItem('c29_discount_code');
            if (discountCode && form.action.includes('/checkout')) {
              e.preventDefault();
              window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
            }
          });
        }
      });
    }

    getCheckoutUrl() {
      if (this.appliedCode) {
        return `/checkout?discount=${encodeURIComponent(this.appliedCode)}`;
      }
      return '/checkout';
    }
  }

  // ========================================
  // Specifications Accordion
  // ========================================
  class C29SpecsAccordion {
    constructor(container) {
      this.container = container;
      this.groups = container.querySelectorAll('.c29-specs-group');
      this.init();
    }

    init() {
      this.groups.forEach(group => {
        const header = group.querySelector('.c29-specs-group-header');
        if (header) {
          header.addEventListener('click', () => this.toggleGroup(group));
        }
      });
    }

    toggleGroup(group) {
      group.classList.toggle('open');
    }
  }

  // ========================================
  // Gallery Handler
  // ========================================
  class C29Gallery {
    constructor(container) {
      this.container = container;
      this.mainImage = container.querySelector('.c29-gallery-main img');
      this.thumbs = container.querySelectorAll('.c29-gallery-thumb');
      this.init();
    }

    init() {
      this.thumbs.forEach((thumb, index) => {
        thumb.addEventListener('click', () => this.selectThumb(thumb, index));
      });

      // 预加载所有大图，使切换时从缓存读取，无需等待网络
      this.preloadImages();
    }

    selectThumb(thumb, index) {
      // Update active state
      this.thumbs.forEach(t => t.classList.remove('active'));
      thumb.classList.add('active');

      // Update main image
      const newSrc = thumb.dataset.fullImage || thumb.querySelector('img')?.src;
      if (newSrc && this.mainImage) {
        this.mainImage.removeAttribute('srcset'); // 清除 srcset，否则浏览器会忽略 src 的改变
        this.mainImage.src = newSrc;
      }
    }

    preloadImages() {
      this.thumbs.forEach(thumb => {
        const src = thumb.dataset.fullImage;
        if (src) {
          const img = new Image();
          img.src = src;
        }
      });
    }
  }

  // ========================================
  // Buy Now Button Handler
  // ========================================
  class C29BuyNowButton {
    constructor(button) {
      this.button = button;
      this.variantId = button.dataset.variantId;
      this.init();
    }

    init() {
      this.button.addEventListener('click', () => this.buyNow());
    }

    async buyNow() {
      // Use dynamically updated variant ID if available
      this.variantId = this.button.dataset.variantId;
      if (!this.variantId) return;

      this.button.disabled = true;
      this.button.style.opacity = '0.7';

      try {
        // Add item to cart first
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            items: [{
              id: parseInt(this.variantId),
              quantity: 1
            }]
          })
        });

        if (response.ok) {
          // Redirect to checkout with discount code if available
          const discountCode = localStorage.getItem('c29_discount_code');
          if (discountCode) {
            window.location.href = `/checkout?discount=${encodeURIComponent(discountCode)}`;
          } else {
            window.location.href = '/checkout';
          }
        } else {
          throw new Error('Failed to add to cart');
        }
      } catch (error) {
        console.error('Buy now error:', error);
        this.button.disabled = false;
        this.button.style.opacity = '1';
        alert('Unable to proceed to checkout. Please try again.');
      }
    }
  }

  // ========================================
  // Brand Tooltip Handler (for mobile)
  // ========================================
  class C29BrandTooltip {
    constructor(container) {
      this.container = container;
      this.trigger = container.querySelector('[data-brand-tooltip-trigger]');
      this.tooltip = container.querySelector('[data-brand-tooltip]');
      this.closeBtn = container.querySelector('[data-brand-tooltip-close]');

      if (this.trigger && this.tooltip) {
        this.init();
      }
    }

    init() {
      // Check if device is touch-based (mobile)
      this.isTouchDevice = window.matchMedia('(hover: none)').matches;

      if (this.isTouchDevice) {
        // Mobile: click to toggle
        this.trigger.addEventListener('click', (e) => {
          e.preventDefault();
          e.stopPropagation();
          this.toggle();
        });

        // Close button
        if (this.closeBtn) {
          this.closeBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            this.close();
          });
        }

        // Close when clicking outside
        document.addEventListener('click', (e) => {
          if (!this.container.contains(e.target)) {
            this.close();
          }
        });
      }
    }

    toggle() {
      if (this.tooltip.classList.contains('active')) {
        this.close();
      } else {
        this.open();
      }
    }

    open() {
      this.tooltip.classList.add('active');
    }

    close() {
      this.tooltip.classList.remove('active');
    }
  }

  // ========================================
  // Variant Picker
  // ========================================
  class C29VariantPicker {
    constructor(container, variants) {
      this.container = container;
      this.variants = variants;
      this.priceEl = document.querySelector('[data-c29-price-current]');
      this.compareEl = document.querySelector('[data-c29-price-compare]');
      this.savingsEl = document.querySelector('[data-c29-price-savings]');
      this.priceBadge = document.querySelector('.c29-price-badge');
      this.priceBlock = document.querySelector('.c29-price-block');
      this.formInput = document.querySelector('#c29-product-form input[name="id"]');
      this.addToCartBtn = document.querySelector('.c29-add-to-cart');
      this.buyNowBtn = document.querySelector('[data-c29-buy-now]');
      this.stockStatus = document.querySelector('.c29-stock-status');
      this.discountContainer = document.querySelector('[data-c29-discount]');
      this.currency = this.discountContainer ? this.discountContainer.dataset.currency : '€';

      this.init();
    }

    init() {
      this.container.addEventListener('change', (e) => {
        if (e.target.matches('input[type="radio"]')) {
          this.onOptionChange(e.target);
        }
      });
    }

    onOptionChange(radio) {
      // Update selected styling
      const group = radio.closest('.c29-variant-options');
      if (group) {
        group.querySelectorAll('.c29-variant-option').forEach(l => l.classList.remove('selected'));
        radio.closest('.c29-variant-option').classList.add('selected');
      }

      // Find matching variant
      const variant = this.getSelectedVariant();
      if (!variant) return;

      this.updatePrice(variant);
      this.updateForm(variant);
      this.updateBuyNow(variant);
      this.updateStock(variant);
      this.updateDiscount(variant);
      this.updateUrl(variant);
    }

    getSelectedVariant() {
      const selected = [];
      this.container.querySelectorAll('input[type="radio"]:checked').forEach(input => {
        selected.push(input.value);
      });

      return this.variants.find(v => {
        for (let i = 0; i < selected.length; i++) {
          const optionKey = 'option' + (i + 1);
          if (v[optionKey] !== selected[i]) return false;
        }
        return true;
      });
    }

    formatMoney(cents) {
      return this.currency + (cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    }

    updatePrice(variant) {
      if (!this.priceEl) return;

      this.priceEl.textContent = this.formatMoney(variant.price);
      this.priceEl.setAttribute('data-original-price', this.formatMoney(variant.price));

      if (variant.compare_at_price && variant.compare_at_price > variant.price) {
        const savings = variant.compare_at_price - variant.price;
        const percent = Math.round((savings / variant.compare_at_price) * 100);

        // Show/create compare price
        if (this.compareEl) {
          this.compareEl.textContent = this.formatMoney(variant.compare_at_price);
          this.compareEl.style.display = '';
        } else if (this.priceBlock) {
          const row = this.priceBlock.querySelector('.c29-price-row');
          if (row) {
            const el = document.createElement('span');
            el.className = 'c29-price-compare';
            el.setAttribute('data-c29-price-compare', '');
            el.textContent = this.formatMoney(variant.compare_at_price);
            row.appendChild(el);
            this.compareEl = el;
          }
        }

        // Badge
        if (this.priceBadge) {
          this.priceBadge.textContent = '-' + percent + '%';
          this.priceBadge.style.display = '';
        }

        // Savings text
        if (this.savingsEl) {
          this.savingsEl.textContent = 'You save ' + this.formatMoney(savings);
          this.savingsEl.style.display = '';
        }
      } else {
        // No compare price — hide elements
        if (this.compareEl) this.compareEl.style.display = 'none';
        if (this.priceBadge) this.priceBadge.style.display = 'none';
        if (this.savingsEl) this.savingsEl.style.display = 'none';
      }
    }

    updateForm(variant) {
      if (this.formInput) {
        this.formInput.value = variant.id;
      }
    }

    updateBuyNow(variant) {
      if (!this.buyNowBtn) return;
      this.buyNowBtn.dataset.variantId = variant.id;
      // Update the C29BuyNowButton instance reference
      this.buyNowBtn.__variantId = variant.id;

      if (variant.available) {
        this.buyNowBtn.style.display = '';
        this.buyNowBtn.disabled = false;
      } else {
        this.buyNowBtn.style.display = 'none';
      }
    }

    updateStock(variant) {
      if (this.addToCartBtn) {
        if (variant.available) {
          this.addToCartBtn.disabled = false;
          this.addToCartBtn.innerHTML = this.addToCartBtn.innerHTML.replace(/Sold Out|Out of Stock/i, 'Add to Cart');
        } else {
          this.addToCartBtn.disabled = true;
          // Replace button text but keep SVG
          const svg = this.addToCartBtn.querySelector('svg');
          const svgHtml = svg ? svg.outerHTML : '';
          this.addToCartBtn.innerHTML = svgHtml + ' Sold Out';
        }
      }

      if (this.stockStatus) {
        if (variant.available) {
          this.stockStatus.style.display = '';
          this.stockStatus.classList.remove('out-of-stock');
        } else {
          this.stockStatus.classList.add('out-of-stock');
          // Update text to "Out of Stock"
          const textSpan = this.stockStatus.querySelector('span');
          if (textSpan) textSpan.textContent = 'Out of Stock';
        }
      }
    }

    updateDiscount(variant) {
      if (!this.discountContainer) return;
      // Update base price for discount handler (European format: 1.099,00)
      const priceInUnits = variant.price / 100;
      // Format as European number for the discount handler parser
      const formatted = priceInUnits.toLocaleString('de-DE', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
      this.discountContainer.dataset.basePrice = formatted;

      // If discount is already applied, re-apply with new base price
      const discountHandler = this.discountContainer.__c29DiscountHandler;
      if (discountHandler && discountHandler.appliedCode) {
        discountHandler.basePrice = priceInUnits;
        // Recalculate amount for percentage discounts
        let newAmount = discountHandler.appliedDiscount;
        if (discountHandler.appliedDiscountType === 'percentage') {
          newAmount = priceInUnits * (discountHandler.appliedDiscountValue / 100);
        }
        discountHandler.applyDiscount(discountHandler.appliedCode, newAmount);
      }
    }

    updateUrl(variant) {
      const url = new URL(window.location);
      url.searchParams.set('variant', variant.id);
      history.replaceState({}, '', url);
    }
  }

  // ========================================
  // Initialize
  // ========================================
  function init() {
    // Initialize discount handler
    const discountContainer = document.querySelector('[data-c29-discount]');
    if (discountContainer) {
      discountContainer.__c29DiscountHandler = new C29DiscountHandler(discountContainer);
    }

    // Initialize variant picker
    const variantPicker = document.querySelector('[data-c29-variant-picker]');
    if (variantPicker) {
      const sectionId = variantPicker.dataset.sectionId;
      const dataEl = document.getElementById('c29-variants-' + sectionId);
      if (dataEl) {
        new C29VariantPicker(variantPicker, JSON.parse(dataEl.textContent));
      }
    }

    // Initialize specs accordion
    const specsContainer = document.querySelector('.c29-specs-accordion');
    if (specsContainer) {
      new C29SpecsAccordion(specsContainer);
    }

    // Initialize gallery
    const galleryContainer = document.querySelector('.c29-gallery');
    if (galleryContainer) {
      new C29Gallery(galleryContainer);
    }

    // Initialize buy now buttons
    document.querySelectorAll('[data-c29-buy-now]').forEach(btn => {
      new C29BuyNowButton(btn);
    });

    // Initialize brand tooltips
    document.querySelectorAll('.c29-brand-badge').forEach(badge => {
      new C29BrandTooltip(badge);
    });

    // Better-offer live chat: open the existing Tidio widget, with contact-page fallback.
    document.querySelectorAll('[data-c29-offer-chat]').forEach(btn => {
      btn.addEventListener('click', () => {
        if (window.tidioChatApi && typeof window.tidioChatApi.open === 'function') {
          window.tidioChatApi.open();
          return;
        }
        if (window.Tidio && typeof window.Tidio.open === 'function') {
          window.Tidio.open();
          return;
        }
        window.location.href = btn.dataset.chatFallback || '/pages/contact';
      });
    });

    // Spare Parts "See More" toggle
    document.querySelectorAll('[data-parts-toggle]').forEach(btn => {
      btn.addEventListener('click', () => {
        const grid = btn.previousElementSibling;
        if (!grid || !grid.classList.contains('c29-parts-grid')) return;
        const expanded = grid.classList.toggle('expanded');
        btn.querySelector('.c29-parts-see-more-text').textContent =
          expanded ? 'Show Less' : 'See More';
      });
    });
  }

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
